//  Reusable scope groups
const baseScopes = ['dashboard', 'style', 'item', 'report', 'sample-requests'];
const extendedScopes = ['style/workflow', 'storybook', 'test'];
const fullCommonScopes = [...baseScopes, ...extendedScopes];

const supplierScopes = ['suppliers', 'purchase-order', 'supplier', 'direct-suppliers', 'brands'];
const adminScopes = [...fullCommonScopes, 'admin'];
const marketingScopes = [...fullCommonScopes, 'marketing'];
const orderScopes = [...fullCommonScopes, 'order'];
const analyticsScopes = [...fullCommonScopes, 'analytics'];
const complianceScopes = [...fullCommonScopes, 'compliance', ...supplierScopes];
const complianceAdminScopes = [...adminScopes, 'compliance', ...supplierScopes];
const personalSettingsScope = ['personal-settings'];
const settingsScope = ['settings'];

const publicScope = ['login', 'supplier/signup', 'supplier/verify'];
const exampleScope = ['example'];
// DL-60478 -- the organization's own D2 migration scheduling scope.
//
// WHY THE `d2-` PREFIX: `checkAccess` (features/accessControl/utils) matches a scope against a
// page with `scope.startsWith(page) || page.startsWith(scope)` -- a two-way prefix match that is
// load-bearing for nested routes ('style/workflow' must satisfy 'style'), so a bare 'migration'
// scope would collide with any 'migration*' page. 'd2-migration' avoids that; the route-level
// gate in app.tsx maps the '/migration' path to it through `getRouteScope`
// (features/accessControl/utils/routeScopes) instead of defaulting to `location.pathname`.
//
// The approvals + preview pages moved to the legacy Sysadmin backoffice (DesignHub.WebMvc), so
// their former FE scopes ('d2-approvals' / 'd2-preview') are gone with the React pages.
//
// The backend remains authoritative for every actual decision; this client scope only avoids
// pointless round-trips and hides buttons that would 403.

// The organization's own migration scheduling page (/d2-migration). Company Admin only --
// requesting a migration is an irreversible, org-wide decision, and the backend gates it on
// OrganizationMigrationOperations.RequestMigration.
const migrationScope = ['d2-migration'];

//  Helper: uniform read/write access
const access = (scopes: string[]) => [
  { verb: 'read', scopes },
  { verb: 'write', scopes },
];

// Helper: read/write with different scopes
const mixedAccess = (readScopes: string[], writeScopes: string[]) => [
  { verb: 'read', scopes: readScopes },
  { verb: 'write', scopes: writeScopes },
];

// Roles Definition
export const roles = [
  { role: 'anonymous', access: access([...publicScope]) },
  {
    role: 'Company User',
    access: access([
      'taskboard',
      ...baseScopes,
      ...publicScope,
      ...personalSettingsScope,
      ...exampleScope,
    ]),
  },
  {
    role: 'Company Admin',
    access: access([
      'taskboard',
      ...adminScopes,
      ...publicScope,
      ...personalSettingsScope,
      ...exampleScope,
      ...supplierScopes,
      ...migrationScope,
    ]),
  },
  {
    role: 'Test User',
    access: mixedAccess(
      ['taskboard', 'dashboard', 'style/workflow', 'storybook', 'supplier', 'test', ...publicScope],
      [...publicScope]
    ),
  },
  {
    role: 'Test Admin',
    access: access([
      'admin',
      ...fullCommonScopes,
      'style/workflow',
      ...publicScope,
      'suppliers',
      'purchase-order',
      'supplier',
    ]),
  },
  {
    role: 'Supplier Admin',
    access: mixedAccess(
      ['storybook', 'test', ...supplierScopes, ...personalSettingsScope, ...settingsScope],
      [...supplierScopes, ...personalSettingsScope, ...settingsScope]
    ),
  },
  {
    // dl-60443: Supplier User may view Settings / company info but not edit (Supplier Admin only).
    role: 'Supplier User',
    access: mixedAccess(
      ['storybook', 'test', ...supplierScopes, ...personalSettingsScope, ...settingsScope],
      [...supplierScopes, ...personalSettingsScope]
    ),
  },
  {
    role: 'Price',
    access: access([...fullCommonScopes, 'style/workflow']),
  },
  {
    role: 'Analytics',
    access: access([...analyticsScopes]),
  },
  {
    role: 'Batch Modifications',
    access: access([...fullCommonScopes, 'style/workflow']),
  },
  {
    role: 'Care Instructions',
    access: access([...fullCommonScopes, 'style/workflow']),
  },
  {
    role: 'Color Admin',
    access: access([...adminScopes, 'style/workflow']),
  },
  {
    role: 'Compliance Admin',
    access: access([...complianceAdminScopes]),
  },
  {
    role: 'Compliance User',
    access: access([...complianceScopes]),
  },
  {
    role: 'Integrator',
    access: access([...fullCommonScopes, 'style/workflow']),
  },
  {
    role: 'Marketing',
    access: access([...marketingScopes]),
  },
  {
    role: 'Order',
    access: access([...orderScopes]),
  },
  {
    role: 'SKU-Barcodes',
    access: access([...fullCommonScopes, 'style/workflow']),
  },
  {
    role: 'Supplier Manager',
    access: access([...adminScopes, 'style/workflow', ...supplierScopes]),
  },
];
